import { createContext, useContext, ReactNode, useEffect, useRef } from 'react'
import { useAuth as useAuthHook, UseAuthReturn } from '@/hooks/useAuth'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/config/api'

interface AuthContextType extends UseAuthReturn {
  isInitialized: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuthHook()
  const queryClient = useQueryClient()
  const wasAuthenticatedRef = useRef(auth.isAuthenticated)

  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current
    if (wasAuthenticated && !auth.isAuthenticated) {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.dispatchers.all })
      queryClient.removeQueries({ queryKey: QUERY_KEYS.precinct.all })
    }
    wasAuthenticatedRef.current = auth.isAuthenticated
  }, [auth.isAuthenticated, queryClient])

  // Show loading spinner during initial auth check
  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  const contextValue: AuthContextType = {
    ...auth,
    isInitialized: !auth.isLoading,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
